import { useCallback, useEffect, useMemo, useState } from 'react';
import { Fulfillment } from 'api/models/Fulfillment';
import { ASC, DESC } from 'common/constants/analyst-dashboards';
import {
  FULFILLMENT_COMPANY_NAME_KEY,
  FULFILLMENT_DATE_KEY,
  FULFILLMENT_MODEL_DUE_DATE_KEY,
  FULFILLMENT_MODEL_STATUS_KEY,
} from 'dashboard409a/common/constants/analyst-dashboard';
import { getAttioTeamMemberDetail, getAttioValuationByRecordId } from 'dashboard409a/services/attio-operation';
import { ModelWithValuationData, UseModelsWithValuationDataProps, UseModelsWithValuationDataReturn } from 'dashboard409a/services/models.types';
import { useModelsList } from '../services/models';

// Constants
const DEFAULT_ORDER_BY = FULFILLMENT_DATE_KEY;
const DEFAULT_ORDER = DESC;
const MODELS_SORT_KEY = 'modelsSort';

export const useModelsWithValuationData = ({
  filterType,
  currentUserRole,
  orderBy = FULFILLMENT_DATE_KEY,
  order = DESC,
}: UseModelsWithValuationDataProps): UseModelsWithValuationDataReturn => {
  // Determine the actual sort order to use (from props, localStorage, or defaults)
  const actualSortOrder = useMemo(() => {
    const localSort = localStorage.getItem(MODELS_SORT_KEY);
    
    // If orderBy/order are different from defaults, use them
    if (orderBy !== DEFAULT_ORDER_BY || order !== DEFAULT_ORDER) {
      return { sortBy: orderBy, sortOrder: order };
    }
    
    // Otherwise, check localStorage
    if (localSort) {
      try {
        const parsed = JSON.parse(localSort);
        return { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder };
      } catch {
        // If parsing fails, use defaults
        return { sortBy: orderBy, sortOrder: order };
      }
    }
    
    // Use defaults
    return { sortBy: orderBy, sortOrder: order };
  }, [orderBy, order]);

  // Fetch models data using the existing API
  const {
    data: modelsData,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useModelsList(filterType, currentUserRole);

  const models = useMemo(() => modelsData?.results || [], [modelsData]);

  // State for valuation data loading
  const [displayedModels, setDisplayedModels] = useState<ModelWithValuationData[] | null>(null);
  const [loadedValuationData, setLoadedValuationData] = useState<Map<string, string>>(new Map());
  
  // Cache for valuation leads to avoid duplicate API calls
  const [valuationLeadCache, setValuationLeadCache] = useState<Map<string, { name: string; imageSrc: string }>>(new Map());

  // Helper function to get valuation lead data (with caching)
  const getValuationLeadData = useCallback(async (valuationLeadRecordId: string): Promise<{ name: string; imageSrc: string }> => {
    // Check cache first
    if (valuationLeadCache.has(valuationLeadRecordId)) {
      return valuationLeadCache.get(valuationLeadRecordId)!;
    }

    try {
      const valuationLeadData = await getAttioTeamMemberDetail(valuationLeadRecordId);
      const leadData = {
        name: valuationLeadData.name || '-',
        imageSrc: valuationLeadData.imageSrc || '-'
      };
      
      // Store in cache
      setValuationLeadCache(prev => new Map(prev.set(valuationLeadRecordId, leadData)));
      
      return leadData;
    } catch {
      // If error getting lead details, return default and cache it
      const defaultData = { name: '-', imageSrc: '-' };
      setValuationLeadCache(prev => new Map(prev.set(valuationLeadRecordId, defaultData)));
      return defaultData;
    }
  }, [valuationLeadCache]);

  // Load initial data when models are loaded
  useEffect(() => {
    if (models && models.length > 0) {
      // Convert models to ModelWithValuationData format
      const modelsWithValuationData: ModelWithValuationData[] = models.map(model => ({
        ...model,
        is_loading_valuation_data: true,
        valuation_date: undefined,
        valuation_lead: undefined,
      }));

      setDisplayedModels(modelsWithValuationData);
    } else if (modelsData) {
      // Models data loaded but empty
      setDisplayedModels([]);
    }
    // If modelsData is null/undefined, keep displayedModels as null (still loading)
  }, [models, modelsData]);

  // Function to load valuation data for a single model
  const loadValuationDataForModel = useCallback(async (model: ModelWithValuationData) => {
    if (!model.attio_valuation_id || loadedValuationData.has(model.id?.toString() || '')) {
      return;
    }

    try {
      // Get valuation details
      const valuationResponse = await getAttioValuationByRecordId(model.attio_valuation_id);
      const valuationDate = valuationResponse.data.values.valuation_date?.[0]?.value || '-';
      const valuationLeadRecordId = valuationResponse.data.values.valuation_lead_94?.[0]?.target_record_id;

      // Get valuation lead details if available (using cache)
      let valuationLead = '-';
      let valuationLeadImageSrc = '';
      if (valuationLeadRecordId) {
        const leadData = await getValuationLeadData(valuationLeadRecordId);
        valuationLead = leadData.name;
        valuationLeadImageSrc = leadData.imageSrc;
      }

      // Update the model with valuation data
      setDisplayedModels(prev => 
        prev?.map(m => 
          m.id === model.id 
            ? { ...m, valuation_date: valuationDate, valuation_lead: valuationLead, valuation_lead_image_src: valuationLeadImageSrc, is_loading_valuation_data: false }
            : m
        ) || []
      );
      setLoadedValuationData(prev => new Map(prev.set(model.id?.toString() || '', valuationDate)));

    } catch {
      // Error loading valuation data for model
      // Update with error state
      setDisplayedModels(prev => 
        prev?.map(m => 
          m.id === model.id 
            ? { ...m, valuation_date: '-', valuation_lead: '-', is_loading_valuation_data: false }
            : m
        ) || []
      );
      setLoadedValuationData(prev => new Map(prev.set(model.id?.toString() || '', '-')));
    }
  }, [loadedValuationData, getValuationLeadData]);

  // Load valuation data for all models that have attio_company_id
  useEffect(() => {
    if (displayedModels && displayedModels.length > 0) {
      const modelsToLoad = displayedModels.filter(
        model => model.attio_valuation_id && !loadedValuationData.has(model.id?.toString() || '')
      );

      if (modelsToLoad.length > 0) {
        // Load data for the first model in the queue
        const modelToLoad = modelsToLoad[0];
        loadValuationDataForModel(modelToLoad);
      }
    }
  }, [displayedModels, loadedValuationData, loadValuationDataForModel]);

  // Function to update a model's note
  const updateModelNote = useCallback((modelId: number, noteContent: string) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model => {
        if (model.id === modelId) {
          return {
            ...model,
            note: {
              id: model.note?.id || 0, // Keep existing ID or use 0 for new notes
              content: noteContent,
              author_id: model.note?.author_id || 0,
              author_email: model.note?.author_email || '',
              author_name: model.note?.author_name || '',
              created_at: model.note?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          };
        }
        return model;
      });
    });
  }, []);

  // Function to update a model's status
  const updateModelStatus = useCallback((modelId: number, newStatus: number) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      
      return prev.map(model => {
        if (model.id === modelId) {
          return {
            ...model,
            model_status: newStatus as Fulfillment.model_status,
          };
        }
        return model;
      });
    });
  }, []);

  // Sort the displayed models based on orderBy and order
  const sortedModels = useMemo(() => {
    if (!displayedModels) {
      return [];
    }
    
    const { sortBy, sortOrder } = actualSortOrder;
    
    // Save to localStorage
    localStorage.setItem(MODELS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));
    
    return [...displayedModels].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;
      
      switch (sortBy) {
        case FULFILLMENT_COMPANY_NAME_KEY:
          aValue = a.company_name || '';
          bValue = b.company_name || '';
          break;
        case FULFILLMENT_MODEL_STATUS_KEY:
          aValue = a.model_status || '';
          bValue = b.model_status || '';
          break;
        case FULFILLMENT_MODEL_DUE_DATE_KEY:
          aValue = (a as unknown as Record<string, unknown>).model_due_date as string || '';
          bValue = (b as unknown as Record<string, unknown>).model_due_date as string || '';
          break;
        case FULFILLMENT_DATE_KEY:
          aValue = a.valuation_date || '';
          bValue = b.valuation_date || '';
          break;
        default:
          aValue = (a as unknown as Record<string, unknown>)[sortBy] as string || '';
          bValue = (b as unknown as Record<string, unknown>)[sortBy] as string || '';
      }
      
      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === ASC ? comparison : -comparison;
      }
      
      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortOrder === ASC ? comparison : -comparison;
      }
      
      // Fallback to string comparison
      const comparison = String(aValue).localeCompare(String(bValue));
      return sortOrder === ASC ? comparison : -comparison;
    });
  }, [displayedModels, actualSortOrder]);

  // Determine if we should show loading
  // Show loading if:
  // 1. Models are still loading, OR
  // 2. displayedModels is null (initial state)
  const shouldShowLoading = isLoadingModels || displayedModels === null;

  return {
    rows: sortedModels,
    totalCount: sortedModels.length,
    isLoading: shouldShowLoading,
    isError: !!modelsError,
    error: modelsError,
    updateModelNote,
    updateModelStatus,
  };
};
